import React from 'react';
import { Image as ImageIcon, RotateCw } from 'lucide-react';

/**
 * ONE rendering of an attachment image, for every surface that shows one.
 *
 * Four states, and none of them is nothing:
 *
 *   loading  fixed-aspect skeleton — reserves the space so the panel does not jump
 *   ready    the image
 *   failed   a visible chip naming the file, plus Try again
 *   gone     a visible chip naming the file, no retry (nothing to retry)
 *
 * THE PROPERTY: the reader always learns a screenshot exists. Both failure chips
 * carry the FILENAME for that reason — "we have something for you and cannot show
 * it" is actionable, where empty space is indistinguishable from no attachment at
 * all. Empty space is what the file-request panels used to render, and a permanent
 * "(unavailable)" with no way back is what chat used to render. Same bug.
 *
 * ARRIVING IS NOT PAINTING. Bytes can be delivered and still fail to decode, which
 * leaves the browser's own broken-image glyph — very nearly the silence this exists
 * to remove. `onError` is wired in every state that mounts an <img>, and the hook
 * decides what that means per transport.
 *
 * THE <img> IS MOUNTED DURING `loading`, hidden behind the skeleton. Direct-URL
 * transport has no other way to discover the outcome: there is no fetch to await,
 * so load/error events on a live element are the only signal. Under blob transport
 * there is no url until the fetch resolves, so the skeleton renders alone and this
 * branch costs nothing.
 */
export function AttachmentImage({ state, filename, onRetry, onLoaded, onLoadError, testId }) {
  const status = state?.status || 'loading';
  const name = filename || 'screenshot';
  const url = state?.url;

  if (status === 'ready' || status === 'loading') {
    const isReady = status === 'ready';
    return (
      <div className={isReady ? '' : 'w-full aspect-[4/3] rounded-md border border-ug-border bg-ug-surface animate-pulse overflow-hidden'}
           data-testid={isReady ? undefined : (testId ? `${testId}-loading` : undefined)}>
        {url && (
          <img src={url} alt={name}
               data-testid={isReady ? testId : undefined}
               onLoad={() => onLoaded && onLoaded()}
               onError={() => onLoadError && onLoadError()}
               className={isReady ? 'w-full rounded-md border border-ug-border block' : 'hidden'} />
        )}
      </div>
    );
  }

  const isGone = status === 'gone';
  return (
    <div data-testid={testId ? `${testId}-${isGone ? 'gone' : 'failed'}` : undefined}
         className="w-full rounded-md border border-ug-border bg-ug-surface px-3 py-2.5">
      <div className="flex items-center gap-2 min-w-0">
        <ImageIcon className="w-3.5 h-3.5 shrink-0 text-slate-500" />
        <span className="font-sans text-[11px] text-zinc-100/70 truncate min-w-0" title={name}>{name}</span>
      </div>
      <p className="font-sans text-[10px] text-slate-500 mt-1">
        {isGone ? 'No longer available.' : 'Couldn’t load this image.'}
      </p>
      {/* `tap-auto` opts out of the global 44px min-height on [data-testid]; without
          it this inline control inflates and breaks the chip. */}
      {!isGone && onRetry && (
        <button onClick={onRetry}
                data-testid={testId ? `${testId}-retry` : undefined}
                className="tap-auto mt-1.5 inline-flex items-center gap-1 rounded border border-ug-border px-2 py-1 font-sans text-[10px] font-semibold text-zinc-100/80 hover:border-ug-gold/50 transition-colors">
          <RotateCw className="w-3 h-3" /> Try again
        </button>
      )}
    </div>
  );
}

export default AttachmentImage;
